import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { OAuthService, UserInfo } from 'angular-oauth2-oidc';
import { asapScheduler, from, merge, Observable, of, race, ReplaySubject } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  first,
  map,
  mapTo,
  observeOn,
  shareReplay,
  skip,
  skipUntil,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import {
  impersonateClient,
  loginSuccess,
  rehydrateState,
  reloadUserInfo,
  signInClient,
  tokenInvalidated,
  userInfoSuccess,
  userProfileSuccess,
  validateSignedInClient
} from './actions/auth.actions';
import { AuthManagedStorageService } from './auth-managed-storage.service';
import { AuthState } from './auth-state';
import { AuthConfig, resolveAuthConfig, ResolvedAuthConfig } from './auth.config';
import { LoadedUser, User, UserAttributes } from './models/user';
import {
  selectCurrentClientId,
  selectImpersonatedClientId,
  selectIsSignedInClientValid,
  selectLoadedUser,
  selectSignedInClientId,
  selectUser
} from './selectors/auth.selectors';
import { UserInfoService } from './user-info.service';
import { UserPermissionService } from './user-permission.service';

interface WfeProxyState {
  source_redirect_url: string;
  targetUrl?: string;
}

interface WfeProxyLogoutState {
  /**
   * The URL of the actual IDP logout endpoint (eg okta)
   */
  logout_url: string;
}

function isNotNullOrUndefined<T>(input: null | undefined | T): input is T {
  return input !== null && input !== undefined;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  readonly config: ResolvedAuthConfig;
  private loginAttemptSubject$ = new ReplaySubject<boolean>(1);
  loginAttempt$ = this.loginAttemptSubject$.asObservable();
  /**
   * Return the current user. Before the user has logged in
   * the user returned represents an anonymous user
   */
  currentUser$: Observable<User>;
  /**
   * Return whether the user is authorized to use this application.
   *
   * This observable will wait until the user is loaded before
   * returning the results of calling the `getIsAuthorizedAppUser`
   * function of the `AuthConfig` object used to configure
   * this library
   *
   * This status will NOT be re-evaluated if the `currentUser$`'s
   * access token used to call the api expires
   *
   */
  isAuthorizedAppUser$: Observable<boolean>;
  /**
   * Return whether there is a selected client id, and that this has been validated against the available client id's for that user.
   */
  isSignedInClientValid$: Observable<boolean>;
  /**
   * An event that will emit the fully populated user.
   *
   * Late subscribers will receive the last value emitted.
   *
   * The user emitted will be composed of:
   * * fields returned by the oidc userinfo endpoint
   * * any fields fetched by `UserInfoService`
   * * an optional `permissions` array field fetched by `UserPermissionService`
   */
  userLoaded$: Observable<LoadedUser>;
  /**
   * Return the current MRI client id, resolved in the following priority order:
   * 1. the impersonated MRI client id (ie value returned by `impersonatedClientId$`) if any
   * 2. the signed in MRI client id (ie value returned by `signedInClientId$`) if any
   * 3. `UserAttributes.clientId` as a fallback — only relevant for legacy auth flows where the
   *    WFE proxy handles client id selection and the value arrives via the user-info endpoint
   *    rather than through an explicit `signInClient` call
   * 4. otherwise `undefined`
   */
  currentClientId$: Observable<string | undefined>;
  /**
   * The MRI client id that should override the client id that the user logged in with.
   *
   * Typically this will enable the scenario where an MRI support team member has logged in
   * and should be authorized to fetch the records belong to another MRI Client
   *
   * Returns `undefined` when no impersonation is in play
   */
  impersonatedClientId$: Observable<string | undefined>;
  /**
   * The MRI client id selected by the user that they wish to be "signed in as".
   * Important: this value should NOT be considered trusted by the server, as it is supplied by client-side.
   * It's critical that the server also perform validation that the user has been assigned to this client
   * EG that the value is included in the user's mri_client_id claim
   *
   * Returns `undefined` when not set
   */
  signedInClientId$: Observable<string | undefined>;
  /**
   * The list of MRI client ids available on the user's Okta profile (`mri_client_id` claim).
   * Emits once the user is fully loaded. Derived from the mri_client_id claim on the user's Okta profile.
   */
  availableClientIds$: Observable<string[]>;
  targetUrlOnLogin?: string;

  constructor(
    private oauthService: OAuthService,
    config: AuthConfig,
    private store: Store<AuthState>,
    private userInfoService: UserInfoService,
    private userPermissionService: UserPermissionService,
    private storage: AuthManagedStorageService
  ) {
    this.config = resolveAuthConfig(config);
    this.configAuth();

    const loginAttemptSuccess$ = this.loginAttempt$.pipe(filter(success => success));

    loginAttemptSuccess$.pipe(map(_ => this.oauthService.getIdentityClaims() as UserInfo)).subscribe(userInfo => {
      store.dispatch(loginSuccess({ userInfo, hasValidAccessToken: true }));
    });

    const tokenReceived$ = this.oauthService.events.pipe(
      filter(e => ['token_received'].includes(e.type)),
      mapTo(true)
    );

    const tokens$ = merge(
      race(loginAttemptSuccess$.pipe(first()), tokenReceived$.pipe(first())),
      loginAttemptSuccess$.pipe(skip(1)),
      tokenReceived$.pipe(skip(1))
    );

    const tokenInvalidated$ = this.oauthService.events.pipe(
      filter(_ => !this.oauthService.hasValidAccessToken()),
      skipUntil(tokens$)
    );
    tokenInvalidated$.subscribe(_ => {
      store.dispatch(tokenInvalidated());
    });

    tokenReceived$
      .pipe(
        switchMap(_ => this.oauthService.loadUserProfile()),
        map(_ => this.oauthService.getIdentityClaims() as UserInfo)
      )
      .subscribe(userInfo => {
        store.dispatch(userProfileSuccess({ userInfo }));
      });

    this.currentUser$ = store.select(selectUser);
    this.currentClientId$ = store.select(selectCurrentClientId);
    this.impersonatedClientId$ = store.select(selectImpersonatedClientId);
    this.isSignedInClientValid$ = store.select(selectIsSignedInClientValid);
    this.signedInClientId$ = store.select(selectSignedInClientId);

    const userAttributes$ = this.createUserAttributes$(tokens$, this.currentUser$, this.isSignedInClientValid$);
    userAttributes$.subscribe(attributes => {
      store.dispatch(userInfoSuccess({ attributes }));
    });

    this.userLoaded$ = store.select(selectLoadedUser).pipe(filter(isNotNullOrUndefined));
    this.availableClientIds$ = this.userLoaded$.pipe(map(user => this.getAvailableClientIds(user)));

    this.isAuthorizedAppUser$ = this.userLoaded$.pipe(
      map(this.config.getIsAuthorizedAppUser ?? (() => true)),
      distinctUntilChanged(),
      shareReplay(1)
    );

    this.autoDetectSignedInClientId();
    this.rehydrateStateFromStorage();
  }

  deleteMriClientIdFromUrl(url: URL, clientId?: string) {
    url.searchParams.delete(this.config.mriClientIdQueryParamName, clientId);
  }

  /**
   * Ensure that login has already occurred and therefore the user object is fully populated.
   * Where login has not already occurred, then initiate the login flow.
   *
   * IMPORTANT: when login has not already occurred, the browser will be redirected to the IDP
   *
   * @param targetUrl The client-side url to redirect to after the user has been authenticated
   * @returns `true` when login has already occurred, `false` otherwise
   * @see `initLoginFlow`
   */
  async ensureLoggedIn(targetUrl?: string) {
    const canAccessApi = await this.loginAttempt$
      .pipe(
        withLatestFrom(this.currentUser$),
        map(([, { hasValidAccessToken }]) => hasValidAccessToken),
        first()
      )
      .toPromise();

    if (!canAccessApi) {
      await this.initLoginFlow(targetUrl);
    }
    return canAccessApi;
  }

  getAvailableClientIds(user: UserInfo): string[] {
    const availableClientIds = user['mri_client_id'];
    return Array.isArray(availableClientIds) ? availableClientIds : [];
  }

  getMriClientIdFromUrl(targetUrl: string | undefined | URL) {
    targetUrl = targetUrl ?? globalThis.location.href;
    if (targetUrl instanceof URL) {
      return targetUrl.searchParams.get(this.config.mriClientIdQueryParamName);
    }

    const isAbsoluteUrl = /^(http|https):\/\//.test(targetUrl);
    const url = new URL(targetUrl, isAbsoluteUrl ? undefined : globalThis.location.origin);
    return url.searchParams.get(this.config.mriClientIdQueryParamName);
  }

  hasMriClientIdInUrl(url: URL, clientId?: string) {
    return url.searchParams.has(this.config.mriClientIdQueryParamName, clientId);
  }

  /**
   * Start the oidc flow via the WFE proxy. This will cause:
   * 1. the browser to be redirected to the IDP
   * 2. where there is NOT already a single-sign-on session with the IDP, the user will be prompted for credentials
   *
   * IMPORTANT: currently this will trigger an implicit-flow. However in the future this will switch
   * to the authorization code flow once OKTA supports rotation of the refresh token.
   *
   * @param targetUrl The client-side url to redirect to after the user has been authenticated
   */
  async initLoginFlow(targetUrl?: string) {
    this.configAuth();

    await this.oauthService.loadDiscoveryDocument();

    const proxyState: WfeProxyState = {
      source_redirect_url: this.oauthService.redirectUri || ''
    };
    if (targetUrl) {
      proxyState.targetUrl = targetUrl;
    }

    const flowOptions = {
      response_mode: 'fragment'
    };

    sessionStorage.setItem('flow', 'pkce');
    this.oauthService.initCodeFlow(JSON.stringify(proxyState), flowOptions);
  }

  isValidSignedInClientId(user: UserInfo, clientId: string) {
    return this.getAvailableClientIds(user).includes(clientId);
  }

  /**
   * Get and clear the target url that was set when the user was redirected to login
   */
  popTargetUrlOnLogin() {
    const targetUrl = this.targetUrlOnLogin;
    this.targetUrlOnLogin = undefined;
    return targetUrl;
  }

  setMriClientIdOnUrl(url: URL, clientId: string) {
    url.searchParams.set(this.config.mriClientIdQueryParamName, clientId);
  }

  /**
   * Change the mri client id that the user has "signed in as" by updating the url and reloading the browser.
   * This will cause the entire app to be reloaded, and the new client id to be picked up from the url.
   *
   * Important: if the user has unsaved changes, these will be lost!
   *
   * @param clientId The new client id to sign in as
   */
  reloadWithSignInClient(clientId: string) {
    // see comment in SignInClientEffects.rejectReassignmentOfSignInClient$ re why we're reloading the app to change client id
    const currentUrl = new URL(globalThis.location.href);
    this.setMriClientIdOnUrl(currentUrl, clientId);
    globalThis.location.href = currentUrl.toString();
  }

  /**
   * Try setting the mri client id that the user has "signed in as" using the one **and only** client id from the list
   * of available values found in the user's mri_client_id claim. Returns true when exactly one value was found and set.
   * A successful value set will be included as a request header to api requests to indicate the client context
   * that the user wishes to operate under.
   * It will also be the value that `currentClientId$` emits
   */
  signInClient(user: UserInfo): boolean;
  /**
   * Set the mri client id that the user has "signed in as", returning whether the value supplied is valid for the user.
   * This value will be included as a request header to api requests to indicate the client context
   * that the user wishes to operate under.
   * It will also be the value that `currentClientId$` emits
   */
  signInClient(user: UserInfo, clientId: string): boolean;
  signInClient(user: UserInfo, clientId?: string) {
    if (clientId !== undefined) {
      const isValid = this.isValidSignedInClientId(user, clientId);
      this.store.dispatch(signInClient({ signedInClientId: clientId, isValid }));
      return isValid;
    }

    const availableClientIds = this.getAvailableClientIds(user);
    if (availableClientIds.length === 1) {
      this.store.dispatch(signInClient({ signedInClientId: availableClientIds[0], isValid: true }));
      return true;
    } else {
      return false;
    }
  }

  /**
   * Attempt to login the user with materials extracted from the url.
   * For authorization code flow, this material will be the authorization
   * code that the library will now make post requests to the STS to exchange
   * the code for the token(s).
   *
   * Note: this will NOT trigger a redirect to the IDP as this method is about
   * implementing the second leg of the oidc/auth2 dance
   */
  async tryLogin() {
    this.configAuth();
    // Q: why call `loadDiscoveryDocumentAndTryLogin` ONLY when we know we haven't already got an access token
    // in storage (probably sessionStorage)?
    // A: in case the angular spa uses a deep link to url that has a code and a state parameter BUT that
    // this link has nothing to do with OIDC (at least for this application/api)
    if (!this.oauthService.hasValidAccessToken()) {
      await this.oauthService.loadDiscoveryDocumentAndTryLogin({});
    } else {
      await this.oauthService.loadDiscoveryDocument();
    }

    if (this.oauthService.state) {
      this.setTargetUrlOnLogin();
    }

    this.oauthService.setupAutomaticSilentRefresh();

    const canAccessApi = this.oauthService.hasValidAccessToken();
    this.loginAttemptSubject$.next(canAccessApi);
    return canAccessApi;
  }

  userLoadedOfType$<T>() {
    return this.userLoaded$ as unknown as Observable<T>;
  }

  validateSignedInClientId(user: User, clientId: string) {
    const isValid = this.isValidSignedInClientId(user, clientId);
    if (isValid) {
      this.store.dispatch(validateSignedInClient());
    }
    return isValid;
  }

  private autoDetectSignedInClientId() {
    if (!this.config.manageClientIdSelection) return;

    const clientIdFromCurrentUrl = this.getMriClientIdFromUrl(globalThis.location.href);
    if (clientIdFromCurrentUrl) {
      this.storage.updateStateItem('signedInClientId', clientIdFromCurrentUrl);
      this.storage.updateStateItem('impersonatedClientId', undefined);
    }
  }

  private configAuth() {
    this.oauthService.configure(this.config);
  }

  private createUserAttributes$(
    tokens$: Observable<boolean>,
    currentUser$: Observable<User>,
    isSignedInClientValid$: Observable<boolean>
  ): Observable<UserAttributes> {
    return tokens$.pipe(
      switchMap(() => {
        if (!this.config.manageClientIdSelection) return of(true);

        // delay fetching user attributes until client id has been set and validated when using email federation flow
        // doing so ensures that:
        // - request to fetch user attributes includes the request header with a valid client id
        // - userLoaded$ emits only when both the user attributes and validated client id are available
        return isSignedInClientValid$.pipe(
          filter(value => value),
          first()
        );
      }),
      switchMap(() => from(this.userInfoService.get()).pipe(withLatestFrom(currentUser$))),
      switchMap(([attributes, user]) =>
        from(this.userPermissionService.get({ ...user, ...attributes })).pipe(
          map(permissions => ({
            ...attributes,
            permissions
          }))
        )
      )
    );
  }

  private rehydrateStateFromStorage() {
    // note: the values returned will always be strings. make sure to convert to correct type as needed before dispatching
    const rawState = this.storage.getItems();

    of(rawState)
      .pipe(observeOn(asapScheduler))
      .forEach(state => {
        this.store.dispatch(rehydrateState(state));
      });
  }

  private setTargetUrlOnLogin() {
    const proxyState = JSON.parse(this.oauthService.state || '{}') as WfeProxyState;
    this.targetUrlOnLogin = proxyState.targetUrl || '';
  }

  getAccessToken(): string {
    return this.oauthService.getAccessToken();
  }

  /**
   * Method - For switching client by support user
   *  to update the clientId in http header
   * for sending the API to fetch the data w.r.t clientId
   */

  impersonateClient(impersonatedClientId: string | undefined) {
    this.store.dispatch(impersonateClient({ impersonatedClientId }));
  }

  /**
   * Method - for refreshing user Info details for already loadedUser
   */
  reloadUserInfo() {
    this.store.dispatch(reloadUserInfo());
  }

  logOut() {
    // note: at this point oauthService.logoutUrl will be set to the value of 'end_session_endpoint' from the oidc discovery document
    if (!this.oauthService.logoutUrl) {
      throw new Error(
        'logoutUrl has not been configured; this might mean that the OpenID Connect Provider has not been configured or does not support OpenID Connect Session Management'
      );
    }

    const proxyState: WfeProxyLogoutState = {
      logout_url: this.oauthService.logoutUrl
    };

    this.oauthService.logOut(false, JSON.stringify(proxyState));
  }
}
